import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ExpandDownLine, UploadLine} from '@dropbox/dig-icons/assets';
import {snackbarAtom} from 'atoms/snackbar';
import cx from 'classnames';
import {
  getPlainTextFromRichTextValue,
  isLexicalJsonString,
  toLexicalJson,
} from 'components/DSYS/editor/utils/lexical';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {useDebouncedValue} from 'hooks/useDebounce';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {EditorState} from 'lexical';
import {
  type CSSProperties,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useEnsureCurrentScorecardSnapshot, useUpdateScorecardSnapshotNotes} from './hooks';
import styles from './ScorecardsTable.module.css';

type ScorecardNotesEditorProps = {
  metricId: number;
  metricSnapshotId?: number | null;
  isCurrentSnapshot?: boolean;
  initialNotes?: string | null;
  notesExpanded?: boolean;
  onNotesExpandedChange?: (expanded: boolean) => void;
};

const SCORECARD_NOTES_COLLAPSED_LINE_COUNT = 2;
const SCORECARD_NOTES_LINE_HEIGHT = 20;
const SCORECARD_NOTES_COLLAPSED_CONTENT_HEIGHT =
  SCORECARD_NOTES_COLLAPSED_LINE_COUNT * SCORECARD_NOTES_LINE_HEIGHT;
const SCORECARD_NOTES_COLLAPSED_CELL_HEIGHT = 56;
const SCORECARD_NOTES_EXPAND_TOGGLE_CLEARANCE = 28;
const SCORECARD_NOTES_OVERFLOW_EPSILON = 1;

const normalizeInitialNotes = (notes?: string | null): string => {
  if (!notes) {
    return '';
  }
  if (isLexicalJsonString(notes)) {
    return notes;
  }
  return toLexicalJson({
    detail: 0,
    format: 0,
    mode: 'normal',
    style: '',
    text: notes,
    type: 'text',
    version: 1,
  });
};

export const ScorecardNotesEditor = ({
  metricId,
  metricSnapshotId,
  isCurrentSnapshot = false,
  initialNotes,
  notesExpanded = false,
  onNotesExpandedChange,
}: ScorecardNotesEditorProps) => {
  const normalizedInitialNotes = useMemo(() => normalizeInitialNotes(initialNotes), [initialNotes]);
  const [ensuredMetricSnapshotId, setEnsuredMetricSnapshotId] = useState<number | null>(null);
  const [notesValue, setNotesValue] = useState(normalizedInitialNotes);
  const [draftNotes, setDraftNotes] = useState(normalizedInitialNotes);
  const [notesOverflowing, setNotesOverflowing] = useState(false);
  const notesCellRef = useRef<HTMLDivElement | null>(null);
  const debouncedNotes = useDebouncedValue(draftNotes, 300);
  const latestNotesRef = useRef(normalizedInitialNotes);
  const lastPersistedNotesRef = useRef(normalizedInitialNotes);
  const pendingNotesRef = useRef<string | null>(null);
  const flushPromiseRef = useRef<Promise<void> | null>(null);
  const isSavingRef = useRef(false);
  const ensureSnapshotPromiseRef = useRef<Promise<number> | null>(null);
  const effectiveMetricSnapshotId = metricSnapshotId ?? ensuredMetricSnapshotId;
  const {mutateAsync: ensureCurrentSnapshot} = useEnsureCurrentScorecardSnapshot();
  const {mutateAsync: updateNotes} = useUpdateScorecardSnapshotNotes();
  const setSnackbar = useSetAtom(snackbarAtom);

  const isEditable = Boolean(effectiveMetricSnapshotId) || isCurrentSnapshot;
  const placeholderText = t('start_writing_placeholder').toString();
  const hasNotes = getPlainTextFromRichTextValue(notesValue).trim().length > 0;

  const measureNotesOverflow = useCallback(() => {
    const contentElement = notesCellRef.current?.querySelector<HTMLElement>('[contenteditable]');
    if (!contentElement) {
      setNotesOverflowing(false);
      return;
    }

    const nextNotesOverflowing =
      contentElement.scrollHeight >
      SCORECARD_NOTES_COLLAPSED_CONTENT_HEIGHT + SCORECARD_NOTES_OVERFLOW_EPSILON;
    setNotesOverflowing((currentNotesOverflowing) =>
      currentNotesOverflowing === nextNotesOverflowing
        ? currentNotesOverflowing
        : nextNotesOverflowing
    );
  }, []);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(measureNotesOverflow);
    return () => window.cancelAnimationFrame(frameId);
  }, [effectiveMetricSnapshotId, measureNotesOverflow, notesExpanded, notesValue]);

  useEffect(() => {
    const notesCell = notesCellRef.current;
    if (!notesCell) {
      return undefined;
    }

    const handleLayoutChange = () => measureNotesOverflow();
    window.addEventListener('resize', handleLayoutChange);

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleLayoutChange);
      resizeObserver.observe(notesCell);
      const contentElement = notesCell.querySelector<HTMLElement>('[contenteditable]');
      if (contentElement) {
        resizeObserver.observe(contentElement);
      }
    }

    handleLayoutChange();

    return () => {
      window.removeEventListener('resize', handleLayoutChange);
      resizeObserver?.disconnect();
    };
  }, [effectiveMetricSnapshotId, measureNotesOverflow]);

  const ensureEditableMetricSnapshotId = useCallback(async () => {
    if (effectiveMetricSnapshotId) {
      return effectiveMetricSnapshotId;
    }

    if (!isCurrentSnapshot) {
      throw new Error('scorecard metric snapshot id is required');
    }

    if (!ensureSnapshotPromiseRef.current) {
      ensureSnapshotPromiseRef.current = ensureCurrentSnapshot(metricId)
        .then((snapshot) => {
          setEnsuredMetricSnapshotId(snapshot.id);
          return snapshot.id;
        })
        .finally(() => {
          ensureSnapshotPromiseRef.current = null;
        });
    }

    return ensureSnapshotPromiseRef.current;
  }, [effectiveMetricSnapshotId, ensureCurrentSnapshot, isCurrentSnapshot, metricId]);

  const flushPendingNotes = useCallback(async () => {
    if (!isEditable) {
      return;
    }

    if (flushPromiseRef.current) {
      await flushPromiseRef.current;
      return;
    }

    const flushPromise = (async () => {
      while (pendingNotesRef.current !== null) {
        const nextNotes = pendingNotesRef.current;
        pendingNotesRef.current = null;

        if (nextNotes === lastPersistedNotesRef.current) {
          continue;
        }

        isSavingRef.current = true;
        try {
          const metricSnapshotIdForSave = await ensureEditableMetricSnapshotId();
          await updateNotes({metricSnapshotId: metricSnapshotIdForSave, notes: nextNotes});
          lastPersistedNotesRef.current = nextNotes;
        } catch {
          if (latestNotesRef.current !== lastPersistedNotesRef.current) {
            pendingNotesRef.current = latestNotesRef.current;
          }
          setSnackbar({text: t('error_saving')});
          break;
        } finally {
          isSavingRef.current = false;
        }
      }
    })();

    flushPromiseRef.current = flushPromise.finally(() => {
      flushPromiseRef.current = null;
    });
    await flushPromiseRef.current;
  }, [ensureEditableMetricSnapshotId, isEditable, setSnackbar, updateNotes]);

  const handleNotesChange = useCallback((editorState: EditorState) => {
    const nextNotes = editorState.toString();
    latestNotesRef.current = nextNotes;
    setNotesValue(nextNotes);
    setDraftNotes(nextNotes);
  }, []);

  useEffect(() => {
    const nextNotes = normalizedInitialNotes;
    const hadLocalPendingChanges =
      latestNotesRef.current !== lastPersistedNotesRef.current ||
      pendingNotesRef.current !== null ||
      isSavingRef.current;

    lastPersistedNotesRef.current = nextNotes;

    if (!hadLocalPendingChanges) {
      if (nextNotes !== notesValue) {
        setNotesValue(nextNotes);
      }
      latestNotesRef.current = nextNotes;
      setDraftNotes(nextNotes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preserve newer local edits while server echoes save responses
  }, [normalizedInitialNotes, effectiveMetricSnapshotId]);

  useEffect(() => {
    if (!isEditable) {
      return;
    }

    if (debouncedNotes !== latestNotesRef.current) {
      return;
    }

    if (debouncedNotes === lastPersistedNotesRef.current) {
      return;
    }

    pendingNotesRef.current = debouncedNotes;
    void flushPendingNotes();
  }, [debouncedNotes, flushPendingNotes, isEditable]);

  useEffect(
    () => () => {
      if (!isEditable || latestNotesRef.current === lastPersistedNotesRef.current) {
        return;
      }

      pendingNotesRef.current = latestNotesRef.current;
      void flushPendingNotes();
    },
    [flushPendingNotes, isEditable]
  );

  const handleToggleNotesExpanded = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onNotesExpandedChange?.(!notesExpanded);
    },
    [notesExpanded, onNotesExpandedChange]
  );

  const notesCellStyle = useMemo<CSSProperties>(
    () => ({
      ...(!isEditable && !notesExpanded ? {height: SCORECARD_NOTES_COLLAPSED_CELL_HEIGHT} : {}),
      ...(notesOverflowing
        ? {
            boxSizing: 'border-box',
            paddingRight: SCORECARD_NOTES_EXPAND_TOGGLE_CLEARANCE,
          }
        : {}),
    }),
    [isEditable, notesExpanded, notesOverflowing]
  );

  return (
    <Box
      ref={notesCellRef}
      className={cx(styles.scorecardNotesCell, {
        [styles.scorecardNotesCellExpanded]: notesExpanded,
        [styles.scorecardNotesCellWithOverflow]: notesOverflowing,
      })}
      width="100%"
      minHeight={isEditable ? '100%' : undefined}
      style={notesCellStyle}
    >
      <RichTextArea
        key={`scorecard-notes-${metricId}-${effectiveMetricSnapshotId ?? 'current'}`}
        autoFocus={false}
        editable={isEditable}
        value={notesValue}
        hideToolbar
        links={false}
        mentions={false}
        disableTables
        disableBorder
        squareBorders
        textSize="small"
        contentClassName={styles.scorecardNotesContent}
        contentEditableHeight={
          isEditable
            ? notesExpanded
              ? 'auto'
              : SCORECARD_NOTES_COLLAPSED_CONTENT_HEIGHT
            : undefined
        }
        editablePaddingX="8"
        editablePaddingY="8"
        truncateLines={
          !isEditable && !notesExpanded ? SCORECARD_NOTES_COLLAPSED_LINE_COUNT : undefined
        }
        not100={notesExpanded}
        placeholder={hasNotes ? null : placeholderText}
        onChange={handleNotesChange}
      />
      {notesOverflowing ? (
        <Box className={styles.scorecardNotesExpandToggle}>
          <Tooltip title={notesExpanded ? 'Collapse notes' : 'Expand notes'} placement="top">
            <IconButton
              variant="borderless"
              size="small"
              aria-label={notesExpanded ? 'Collapse notes' : 'Expand notes'}
              aria-expanded={notesExpanded}
              onClick={handleToggleNotesExpanded}
            >
              <UIIcon
                src={notesExpanded ? UploadLine : ExpandDownLine}
                size="small"
                className={atoms({color: 'Text Base'})}
              />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Box>
  );
};
